// Passing Style widget (Players canvas, `player-passing-style`) — old Catalyst's
// "Style" card: two stacked share bars answering "what's his passing style?" —
// the Attempt Mix (how hard the passes he tries are) and the Direction Mix
// (forward / sideways / backward), each segment labelled with his share and the
// position-group league average. Positional basis; shares the peer query.
//
// The share keys are 0–1 fractions in the data (verified: Messi MLS 2025 striker
// hard 0.16 / med 0.24 / easy 0.60); stackedShares normalises the bar widths.

import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import { num, stackedShares, type StatsRow } from "./passing.js";
import { usePassingComparison } from "./usePassingComparison.js";
import PassingScope from "./PassingScope.js";

interface Seg {
  key: string;
  label: string;
  color: string;
}

// Colours echo old Catalyst: attempt purple/blue/green, direction pink/yellow/blue.
const ATTEMPT: Seg[] = [
  { key: "hard_pass_perc", label: "Hard", color: "#a78bfa" },
  { key: "medium_pass_perc", label: "Medium", color: "#60a5fa" },
  { key: "easy_pass_perc", label: "Easy", color: "#34d399" },
];
const DIRECTION: Seg[] = [
  { key: "forward_pass_perc", label: "Forward", color: "#f9a8d4" },
  { key: "sideways_pass_perc", label: "Sideways", color: "#fde047" },
  { key: "backward_pass_perc", label: "Backward", color: "#60a5fa" },
];
const ALL_KEYS = [...ATTEMPT, ...DIRECTION].map((s) => s.key);

export default function PassingStyleWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  const cmp = usePassingComparison(playerId, competitionId, seasonId, ALL_KEYS);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null)
    return <Msg>Pick a competition and season above.</Msg>;
  if (cmp.isPending) return <Msg>Loading style…</Msg>;
  if (cmp.isError) return <Msg danger>Couldn't load style.</Msg>;
  if (!cmp.playerRow) return <Msg>No passing data for this player in this season.</Msg>;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 14 }}>
      <PassingScope cmp={cmp} />
      <Mix title="Attempt mix" segs={ATTEMPT} row={cmp.playerRow} avg={cmp.peerAvg} />
      <Mix title="Direction mix" segs={DIRECTION} row={cmp.playerRow} avg={cmp.peerAvg} />
    </div>
  );
}

function Mix({ title, segs, row, avg }: { title: string; segs: Seg[]; row: StatsRow; avg: StatsRow | null }) {
  const shares = stackedShares(row, segs.map((s) => s.key));
  if (shares.length === 0) return null;
  const byKey = new Map(shares.map((s) => [s.key, s]));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ color: theme.inkFaint, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {title}
      </div>
      {/* Stacked bar — segment widths normalised to fill. */}
      <div style={{ display: "flex", height: 22, borderRadius: 6, overflow: "hidden" }}>
        {segs.map((s) => {
          const w = byKey.get(s.key)?.widthPct ?? 0;
          return <div key={s.key} style={{ width: `${w}%`, background: s.color }} title={`${s.label}`} />;
        })}
      </div>
      {/* Legend: player share (bold) + position-group average under each. */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${segs.length}, 1fr)`, gap: 8 }}>
        {segs.map((s) => {
          const player = byKey.get(s.key)?.value ?? 0;
          const posAvg = num(avg, s.key);
          return (
            <div key={s.key} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: theme.inkMuted }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: s.color, display: "inline-block" }} />
                {s.label}{" "}
                <span style={{ color: theme.ink, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                  {(player * 100).toFixed(1)}%
                </span>
              </span>
              <span style={{ color: theme.inkFaint, fontSize: 11 }}>
                Pos avg {posAvg === null ? "—" : `${(posAvg * 100).toFixed(1)}%`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
